import './history.css';

import { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { allUserConfigsState } from '../../recoil/atoms/allUserConfigsState';
import { useOpenGlobalModal } from '../../recoil/hooks/useOpenGlobalModal';

import { translate } from '../../../common/language/translations';
import { InputField } from '../controls/input/InputField';
import { Activity } from '../../types/activity';
import { Team } from '../../types/team';
import { getDisplayPlayerName, getShortUserName } from '../../utils/nameUtils';
import { dateStringToTextFormat } from '../../utils/dateUtils';
import { shouldShowDateForHistoryEntry } from '../../utils/historyUtils';


interface TeamHistoryProps {
  teamId: string;
  team: Team;
  history: Activity[];
  handleAddActivity: (activity: Activity) => void;
  handleDeleteActivity: (activityId: string) => void;
}

export const TeamHistory: React.FC<TeamHistoryProps> = ({
  teamId,
  team,
  history,
  handleAddActivity,
  handleDeleteActivity,
}) => {

  const userConfig = useRecoilValue(userConfigState);
  const allUsersWithSameTenant = useRecoilValue(allUserConfigsState);

  const { openConfirmModal } = useOpenGlobalModal();

  const inputRef = useRef<HTMLInputElement>(null);
  const [commentString, setCommentString] = useState('');

  const [showHistory, setShowHistory] = useState(false);


  const onChangeCommentString = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCommentString(event.target.value);
  };


  const onKeyDownCommentString = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && commentString.length > 0) {
      submitNewComment();
    }
  };


  const onCommentClick = (activityId: string | undefined, isCurrentUser: boolean) => {
    if (userConfig && isCurrentUser && activityId) {
      openConfirmModal(
        () => handleDeleteActivity(activityId),
        'deleteComment',
        'noRegretInfo'
      );
    }
  };


  const submitNewComment = () => {
    if (!userConfig) return;

    const activity: Activity = {
      userEmail: userConfig.email,
      date: new Date().toISOString(),
      teamId: teamId,
      teamName: team.name,
      teamIsAcademyTeam: team.isAcademyTeam ?? false,
      teamOwner: team.teamOwner ?? null,
      actionType: 'teamCommented',
      comment: commentString,
    };

    handleAddActivity(activity);

    setCommentString('');
    if (inputRef.current) {
      inputRef.current.blur();
    }
    setTimeout(() => {
      scrollToBottom();
    }, 250);
  };


  const scrollToBottom = () => {
    const commentField = document.querySelector('.team-history-action-section');
    if (commentField) {
      commentField.scrollTop = commentField.scrollHeight;
    }
  };


  const getHistoryInfoText = (activity: Activity, isCurrentUser: boolean) => {
    if (!userConfig) return '';

    const userName = isCurrentUser
      ? translate('you', userConfig.language)
      : allUsersWithSameTenant[activity.userEmail]
        ? getShortUserName(allUsersWithSameTenant[activity.userEmail].name)
        : translate('unknownUser', userConfig.language);

    if (activity.actionType === 'teamCreated') {
      return userName + ' ' + translate('<someone>createdTheTeam', userConfig.language);
    }

    if (activity.actionType === 'teamArchived') {
      return userName + ' ' + translate('<someone>archivedTheTeam', userConfig.language);
    }

    if (activity.actionType === 'teamCommented') {
      return userName + ' ' + translate('<someone>commented', userConfig.language) + ':';
    }

    if (activity.actionType === 'teamFormationEdited') {
      return userName
        + ' '
        + translate('<someone>editedTheFormationTo', userConfig.language)
        + ' '
        + activity.newFormation;
    }

    if (activity.actionType === 'teamNameEdited') {
      return userName
        + ' '
        + translate('<someone>editedTheNameTo', userConfig.language)
        + ' '
        + activity.newName;
    }

    if (activity.playerName) {
      if (activity.actionType === 'playerAdded') {
        return userName
          + ' '
          + translate('<someone>added<thePlayer>', userConfig.language)
          + ' '
          + getDisplayPlayerName(activity.playerName, 16);
      }

      if (activity.actionType === 'playerRemoved') {
        return userName
          + ' '
          + translate('<someone>removed<thePlayer>', userConfig.language)
          + ' '
          + getDisplayPlayerName(activity.playerName, 16);
      }
    }

    return activity.actionType;
  };


  useEffect(() => {
    const timer = setTimeout(() => {
      setShowHistory(true);
    }, 250);

    return () => clearTimeout(timer);
  }, []);


  useEffect(() => {
    scrollToBottom();
  }, []);


  return (
    <div className='history-container team-history-container'>

      <div className={'history-action-section team-history-action-section' + (showHistory ? ' history-action-section-show' : '')}>
        {history.map((activity, index) => {

          const isCurrentUser = userConfig !== null && activity.userEmail === userConfig.email;

          return (
            <div
              key={index}
              className='history-action team-history-action'>

              {shouldShowDateForHistoryEntry(index, history) && (
                <div className={'history-date-row team-history-date-row' + (index === 0 ? ' team-history-first-date-row' : '')}>

                  {isCurrentUser && <div className='history-date-small-divider' />}

                  <div className={'history-date-small' + (isCurrentUser ? ' history-date-current-user' : '')}>
                    {dateStringToTextFormat(activity.date.split('T')[0])}
                  </div>

                  {!isCurrentUser && <div className='history-date-small-divider' />}

                </div>
              )}

              {(activity.actionType !== 'teamCommented' || !isCurrentUser) && (
                <div
                  className={
                    'team-history-info-text'
                    + (isCurrentUser ? ' team-history-info-text-current-user' : '')
                    + (activity.actionType === 'teamCommented' ? ' team-history-info-text-comment' : '')
                  }
                >
                  {getHistoryInfoText(activity, isCurrentUser)}
                </div>
              )}

              {activity.actionType === 'teamCommented' && (
                <div
                  className={'team-history-comment' + (isCurrentUser ? ' team-history-comment-current-user' : '')}
                  onClick={() => onCommentClick(activity.id, isCurrentUser)}
                >
                  {activity.comment}
                </div>
              )}

            </div>
          );
        })}
      </div>

      <div className='team-history-input-section'>
        <InputField
          ref={inputRef}
          value={commentString}
          placeholder={translate('provideComment', userConfig?.language)}
          placeholderFontSize={12}
          onChange={onChangeCommentString}
          onKeyDown={onKeyDownCommentString}
          resetValue={() => setCommentString('')}
          onClick={scrollToBottom}
          isSquare={true}
        />
      </div>

    </div>
  );
};
